import { useEffect, useState, useRef } from "react";
import type { TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/carousel";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5600);
    return () => window.clearInterval(timer);
  }, [paused]);

  const total = heroSlides.length;
  const goTo = (next: number) => setIndex((next + total) % total);

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 45) return;
    goTo(delta > 0 ? index - 1 : index + 1);
  };

  return (
    <section
      aria-label="焦点内容轮播"
      className="relative w-full overflow-hidden rounded-lg bg-accent-950 h-[420px] md:h-[500px] lg:h-[560px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {heroSlides.map((slide, slideIndex) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
          }`}
          aria-hidden={slideIndex !== index}
        >
          <img
            src={slide.image}
            alt={`${slide.title} - age动漫焦点图`}
            title={`${slide.title} - age动漫精选内容`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-accent-950/90 via-accent-950/45 to-accent-950/20"></div>
          <div className="absolute inset-x-0 bottom-0 p-4 pb-12 md:p-8 md:pb-16 lg:p-10 lg:pb-20">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 rounded bg-primary-500 text-background-50 text-[11px] font-medium">
                  {slide.badge}
                </span>
                <span className="text-[11px] md:text-xs tracking-[0.14em] text-background-200 uppercase">
                  {slide.kicker}
                </span>
              </div>
              <h2 className="text-xl md:text-3xl lg:text-[34px] font-bold text-background-50 leading-snug">
                {slide.title}
              </h2>
              <p className="mt-2 md:mt-3 text-sm md:text-base text-background-200/85 leading-relaxed line-clamp-2 max-w-xl">
                {slide.description}
              </p>
              <Link
                to={slide.link}
                className="inline-flex items-center gap-1.5 mt-4 md:mt-6 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {slide.linkLabel}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      ))}

      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-950/50 backdrop-blur-sm text-background-100 text-[11px]">
        <span className="font-heading">{String(index + 1).padStart(2, "0")}</span>
        <span className="text-background-400">/</span>
        <span className="text-background-300">{String(total).padStart(2, "0")}</span>
      </div>

      <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一张"
          className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一张"
          className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      <div className="absolute bottom-5 left-4 md:bottom-10 md:left-10 z-20 flex items-center gap-1.5">
        {heroSlides.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(slideIndex)}
            aria-label={`切换到第 ${slideIndex + 1} 张`}
            aria-current={slideIndex === index}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              slideIndex === index ? "w-6 bg-primary-400" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}